import { Placement } from '@floating-ui/react';
import type {
  ActiveHotspot,
  DependencyExpression,
  HotspotKind,
  MountedTarget,
  OffsetStyle,
  OnboardingFlow,
  OnboardingHotspotConfig,
} from './types';

/**
 * Checks whether a hotspot dependency expression has been satisfied.
 *
 * Supported dependency shapes:
 * - undefined: no dependency, always fulfilled
 * - string: a single hotspot key must be completed
 * - { all: [...] }: every child dependency must be fulfilled
 * - { any: [...] }: at least one child dependency must be fulfilled
 */
function isDependencyFulfilled(expression: DependencyExpression | undefined, completedHotspots: Set<string>): boolean {
  if (expression === undefined) return true;
  if (typeof expression === 'string') return completedHotspots.has(expression);

  if ('all' in expression) {
    return expression.all.every((child) => isDependencyFulfilled(child, completedHotspots));
  }

  if (expression.any.length === 0) return false;
  return expression.any.some((child) => isDependencyFulfilled(child, completedHotspots));
}

/**
 * Finds the first eligible hotspot for a target and kind.
 *
 * Flow priority decides which flow wins when multiple flows define hotspots
 * for the same target. Higher priority flows are checked first.
 */
function getEligibleHotspotForTarget(
  flows: OnboardingFlow[],
  target: string,
  kindFilter: HotspotKind,
  completedHotspots: Set<string>
): OnboardingHotspotConfig | undefined {
  const flowsByPriority = [...flows].sort((a, b) => b.priority - a.priority);

  for (const flow of flowsByPriority) {
    const match = flow.hotspots.find((hotspot) => {
      const kind = hotspot.kind ?? 'guided';

      if (kind !== kindFilter) return false;
      if (hotspot.target !== target) return false;
      if (completedHotspots.has(hotspot.key)) return false;

      return isDependencyFulfilled(hotspot.dependencies, completedHotspots);
    });

    if (match) return match;
  }
  return undefined;
}

/**
 * Resolves the active guided hotspot.
 *
 * Only one guided hotspot can be active at a time. Mounted targets are checked
 * from latest to earliest so the most recently mounted matching target wins.
 */
export function resolveActiveHotspot(
  flows: OnboardingFlow[],
  mountedTargets: MountedTarget[],
  completedHotspots: Set<string>
): ActiveHotspot | null {
  for (let index = mountedTargets.length - 1; index >= 0; index--) {
    const { target, instanceId } = mountedTargets[index];

    const match = getEligibleHotspotForTarget(flows, target, 'guided', completedHotspots);

    if (match) {
      return {
        key: match.key,
        target: match.target,
        kind: 'guided',
        tooltip: match.tooltip,
        instanceId,
        tracking: match.tracking,
      };
    }
  }
  return null;
}

/**
 * Resolves all active info hotspots.
 *
 * Info hotspots are educational and can be shown independently, so unlike
 * guided hotspots, multiple info hotspots may be active at the same time.
 */
export function resolveActiveInfoHotspots(
  flows: OnboardingFlow[],
  mountedTargets: MountedTarget[],
  completedHotspots: Set<string>
): ActiveHotspot[] {
  const flowsByPriority = [...flows].sort((a, b) => b.priority - a.priority);
  const seenHotspotKeys = new Set<string>();
  const activeInfoHotspots: ActiveHotspot[] = [];

  for (const { target, instanceId } of mountedTargets) {
    for (const flow of flowsByPriority) {
      const match = flow.hotspots.find(
        (hotspot) =>
          hotspot.target === target &&
          hotspot.kind === 'info' &&
          !completedHotspots.has(hotspot.key) &&
          isDependencyFulfilled(hotspot.dependencies, completedHotspots)
      );

      if (!match || seenHotspotKeys.has(match.key)) continue;

      seenHotspotKeys.add(match.key);

      activeInfoHotspots.push({
        key: match.key,
        target: match.target,
        kind: 'info',
        tooltip: match.tooltip,
        instanceId,
        tracking: match.tracking,
      });
    }
  }
  return activeInfoHotspots;
}

export function getOffsetStyles(offset?: OffsetStyle) {
  if (!offset) return undefined;

  return {
    top: offset.top !== undefined ? `${offset.top}px` : undefined,
    right: offset.right !== undefined ? `${offset.right}px` : undefined,
    bottom: offset.bottom !== undefined ? `${offset.bottom}px` : undefined,
    left: offset.left !== undefined ? `${offset.left}px` : undefined,
  };
}

export function getArrowPlacementStyle(placement: Placement = 'top') {
  switch (placement) {
    case 'top':
      return {
        margin: 'margin-top: -1px;',
      };
    case 'bottom':
      return {
        margin: 'margin-bottom: -1px;',
      };
    case 'left':
      return {
        margin: 'margin-left: -1px;',
      };
    case 'right':
      return {
        margin: 'margin-right: -1px;',
      };
    default:
      return {
        margin: '',
        clipPath: '',
      };
  }
}

/**
 * Creates namespaced hotspot keys.
 *
 * Example:
 * createHotspotKeys('users', { ACTION_BUTTON: 'action-button' })
 * returns:
 * { ACTION_BUTTON: 'users:action-button' }
 */
export function createHotspotKeys<TNamespace extends string, const TKeys extends Record<string, string>>(
  namespace: TNamespace,
  keys: TKeys
): { readonly [K in keyof TKeys]: `${TNamespace}:${TKeys[K]}` } {
  return Object.fromEntries(Object.entries(keys).map(([name, value]) => [name, `${namespace}:${value}`])) as {
    readonly [K in keyof TKeys]: `${TNamespace}:${TKeys[K]}`;
  };
}

/**
 * Marks all hotspots for a target as completed.
 *
 * This is useful when a user interacts with a target and that interaction should
 * complete every hotspot attached to that target.
 *
 * Also applies chained hotspot completion through the `completes` property.
 */
export function applyTargetCompletion(flow: OnboardingFlow, target: string, completedHotspots: Set<string>): boolean {
  let matched = false;

  for (const hotspot of flow.hotspots) {
    if (hotspot.target !== target) continue;

    matched = true;
    completedHotspots.add(hotspot.key);

    hotspot.completes?.forEach((k) => completedHotspots.add(k));
  }
  return matched;
}

/**
 * Returns hotspot keys that should be treated as completed on initial flow registration because their groups are already completed.
 */
export function getInitialCompletedHotspotKeys(flow: OnboardingFlow, completedGroups: string[]): string[] {
  const completedGroupSet = new Set(completedGroups);
  return flow.hotspots.filter((hotspot) => completedGroupSet.has(hotspot.group)).map((hotspot) => hotspot.key);
}
/**
 * Derives groups that became complete based on the latest completed hotspot set.
 *
 * This intentionally returns only newly completed groups, not all completed
 * groups. Already completed groups are filtered out using alreadyCompletedGroups.
 *
 * A group can become complete in two ways:
 *
 * 1. Required hotspot completion:
 *    Every non-info hotspot in the group is completed.
 *
 * 2. Explicit group completion:
 *    A completed hotspot declares `completeGroups`, which marks those groups
 *    complete directly.
 */
export function getNewlyCompletedGroups({
  flow,
  completedHotspots,
  alreadyCompletedGroups,
}: {
  flow: OnboardingFlow;
  completedHotspots: Set<string>;
  alreadyCompletedGroups: string[];
}): string[] {
  const alreadyCompletedGroupSet = new Set(alreadyCompletedGroups);

  const groupsCompletedByRequiredHotspots = getGroupsCompletedByRequiredHotspots({
    flow,
    completedHotspots,
    alreadyCompletedGroup: alreadyCompletedGroupSet,
  });

  const groupsCompletedByCompleteGroups = getGroupsCompletedByCompleteGroups({
    flow,
    completedHotspots,
    alreadyCompletedGroup: alreadyCompletedGroupSet,
  });

  return [...new Set([...groupsCompletedByRequiredHotspots, ...groupsCompletedByCompleteGroups])];
}

/**
 * Finds groups where every required hotspot has been completed.
 *
 * Info hotspots are excluded because they are educational hints and should not block group completion.
 */
function getGroupsCompletedByRequiredHotspots({
  flow,
  completedHotspots,
  alreadyCompletedGroup,
}: {
  flow: OnboardingFlow;
  completedHotspots: Set<string>;
  alreadyCompletedGroup: Set<string>;
}): string[] {
  const requiredHotspotKeysByGroup = groupRequiredHotspotKeysByGroup(flow.hotspots);

  return Object.entries(requiredHotspotKeysByGroup)
    .filter(([group, hotspotKeys]) => {
      if (alreadyCompletedGroup.has(group)) return false;

      return hotspotKeys.every((hotspotKey) => completedHotspots.has(hotspotKey));
    })
    .map(([group]) => group);
}

/**
 * Finds groups completed through a hotspot's `completeGroups` property.
 *
 * This supports flows where completing one hotspot should complete one or more groups directly, even if those groups have multiple hotspots.
 */
function getGroupsCompletedByCompleteGroups({
  flow,
  completedHotspots,
  alreadyCompletedGroup,
}: {
  flow: OnboardingFlow;
  completedHotspots: Set<string>;
  alreadyCompletedGroup: Set<string>;
}): string[] {
  const completedGroups = new Set<string>();

  flow.hotspots.forEach((hotspot) => {
    if (!completedHotspots.has(hotspot.key)) return;
    if (!hotspot.completeGroups) return;

    hotspot.completeGroups.forEach((group) => {
      if (alreadyCompletedGroup.has(group)) return;

      completedGroups.add(group);
    });
  });

  return [...completedGroups];
}

/**
 * Groups required hotspot keys by group/task id.
 *
 * Required hotspots are all non-info hotspots.
 * Info hotspots are educational only, so they should not be required for:
 * - completing an onboarding group
 * - marking a checklist task as complete
 *
 * Undefined kind is treated as guided by default.
 */
export function groupRequiredHotspotKeysByGroup(hotspots: OnboardingHotspotConfig[]): Record<string, string[]> {
  return hotspots.reduce<Record<string, string[]>>((hotspotKeysByGroup, hotspot) => {
    const kind = hotspot.kind ?? 'guided';

    if (kind === 'info') return hotspotKeysByGroup;

    (hotspotKeysByGroup[hotspot.group] ??= []).push(hotspot.key);

    return hotspotKeysByGroup;
  }, {});
}

/**
 * A task is complete when:
 * - its group is already persisted as completed, or
 * - all required hotspots in that group are completed.
 */
export function isTaskCompleted({
  taskId,
  requiredHotspotKeys,
  completedHotspots,
  completedGroupSet,
}: {
  taskId: string;
  requiredHotspotKeys: string[];
  completedHotspots: Set<string>;
  completedGroupSet: Set<string>;
}): boolean {
  if (completedGroupSet.has(taskId)) return true;

  return requiredHotspotKeys.length > 0 && requiredHotspotKeys.every((hotspotKey) => completedHotspots.has(hotspotKey));
}
